//#region Imports Packages and Libs

import { Component, Vue, Prop, Watch } from "vue-facing-decorator";
import type { MessageStructureComponent, MessageContentSettings } from "../types";
import { store } from "../store/store";
import { STORE_MUTATION } from "../enums/store_mutation";
import Button from "../components/button.vue";
import Img from "../components/image.vue";
import FormInput from "../components/form_input.vue";
import { Util } from "../util";
import { __vueEvent } from "../constant";
import { EVENTS } from "../enums/events";
import FormContainer from "../components/forms/form_container.vue";
import { MESSAGE_COMPONENT_TYPE } from "../enums";
import { isMobile } from "../helpers/is_mobile";

//#endregion

//#region Enums

/**
 * Enum for Modal Load Behavior
 */
enum LoadBehaviorEnum {
	PLACEHOLDER_ONLY = "placeholder_only",
	AUTO_OPEN_WITH_PLACEHOLDER = "auto_open_with_placeholder",
	AUTO_OPEN_WITHOUT_PLACEHOLDER = "auto_open_without_placeholder",
}

//#endregion

//#region Define the component in class-style

declare var marked;
@Component({
	components: { Button, Img, FormInput, FormContainer },
})
export default class Modal extends Vue {
	//#region Class properties will be component data

	@Prop() messageId?: any;
	showModal = false;
	@Prop() messageSettings?: any;
	@Prop() messageContentsettings?: MessageContentSettings;
	@Prop() components?: MessageStructureComponent[];
	//timeoutId!: ReturnType<typeof setTimeout> | undefined;
	placeholderLabel!: string;
	placeholderIcon!: string;
	placeholderIconSize!: string;
	isPlaceholderIconImage!: boolean;
	LoadBehaviorEnum = LoadBehaviorEnum;

	//#endregion

  //#region Computed Properties

  get isBotHasAvatar() {
    return this.$store.state.isBotHasAvatar || false;
  }

  get typingType() {
    return this.$store.state.typingType || 0;
  }

  get isUserHasAvatar() {
    return this.$store.state.isUserHasAvatar || false;
  }

  //#endregion

	//#region Modal Lifecycle Hooks

	created() {
		this.setModalSettings();
	}

	mounted() {
		this.listenEvents();
	}

	/**
	 * This computed prop is used to get the icon image style.
	 */
	get iconImageStyle() {
		return {
			width: this.placeholderIconSize || "25px",
			height: this.placeholderIconSize || "25px",
			display: "flex",
		};
	}

	/**
	 * This computed prop is used to get unique id for the modal description.
	 */
	get modalDescriptionId() {
		return `modalDescription-${this.messageId}`;
	}

	/**
	 * This computed prop is used to get unique id for the chat modal
	 */
	get chatModal() {
		return this.$refs.chat_modal as HTMLElement;
	}

	/**
	 * Build render list with button runs grouped as type "button-group".
	 * - Consecutive buttons/links are grouped only when run length > 1.
	 * - Single buttons remain unchanged (no list container).
	 */
	get renderComponents() {
		type ButtonGroup = { type: "button-group"; items: MessageStructureComponent[] };
		type RenderComponent = MessageStructureComponent | ButtonGroup;

		try {
			const components = this.components;
			if (!Array.isArray(components) || components.length === 0) return [] as RenderComponent[];

			const result: RenderComponent[] = [];
			let run: MessageStructureComponent[] = []; // current button run

			// Helper to flush current run of buttons (if any) to result. Flush means process and clear.
			const flushRun = () => {
				// emit button-group only for 2+ buttons
				if (run.length > 1) {
					result.push({ type: "button-group", items: run });
				} else if (run.length === 1) {
					result.push(run[0]);
				}
				run = [];
			};

			for (const component of components) {
				const isButton = component && (component.type === "button" || component.type === "link");
				if (isButton) {
					run.push(component);
					continue;
				}

				// non-button breaks run; keep order
				flushRun();
				result.push(component);
			}

			// final flush after loop
			flushRun();
			return result;
		} catch (error) {
			console.log("[modal.ts] An error has occurred \n-> Inside renderComponents -> Error: ", error);
			return this.components;
		}
	}

	//#endregion

	//#region Modal related logic here

	listenEvents() {
		__vueEvent.on(EVENTS.ButtonClick, this.hideModal);
		this.handleModalKeydown();
    this.listenEventsForIFrame();
	}

	/**
	 * This method is used to get the svg icon for the placeholder.
	 */
	getSvgIcon() {
		return `<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 122.88 122.87" style="enable-background:new 0 0 122.88 122.87" xml:space="preserve"
                width="${this.placeholderIconSize || '25px'}" height="${this.placeholderIconSize || '25px'}" aria-hidden="true">
            <g>
            <path d="M122.88,77.63v41.12c0,2.28-1.85,4.12-4.12,4.12H77.33v-9.62h35.95c0-12.34,0-23.27,0-35.62H122.88L122.88,77.63z M77.39,9.53V0h41.37c2.28,0,4.12,1.85,4.12,4.12v41.18h-9.63V9.53H77.39L77.39,9.53z M9.63,45.24H0V4.12C0,1.85,1.85,0,4.12,0h41 v9.64H9.63V45.24L9.63,45.24z M45.07,113.27v9.6H4.12c-2.28,0-4.12-1.85-4.12-4.13V77.57h9.63v35.71H45.07L45.07,113.27z"></path>
            </g>
        </svg>`;
	}

	/**
	 * The function checks if an image exists at a given URL and calls a callback function with a
	 * boolean value indicating the result.
	 * @param url - The URL of the image you want to check if it exists.
	 * @param callback - The callback parameter is a function that will be called once the image has
	 * been checked for existence. It takes a single argument, a boolean value indicating whether the
	 * image exists or not.
	 */
	checkIfImageExists(url, callback) {
		try {
			const img = new Image();
			img.src = url;

			if (img.complete) {
				callback(true);
			} else {
				img.onload = () => {
					callback(true);
				};

				img.onerror = () => {
					callback(false);
				};
			}
		} catch (ex) {
			callback(false);
			console.log("[modal.ts] An error has occurred \n-> Inside checkIfImageExists -> Error: ", ex);
		}
	}

	/**
	 * This method is used to set the modal settings.
	 */
	setModalSettings() {
		try {
			// Set the default placeholder label and icon
			this.placeholderLabel = "Show Modal";
			this.placeholderIconSize = "25px";

			// Set the placeholder label and icon
			if (this.messageSettings && Object.keys(this.messageSettings).length !== 0) {
				this.placeholderLabel = this.messageSettings.placeholderLabel || "Show Modal";
				this.placeholderIcon = this.messageSettings.placeholderIcon || "";
				this.placeholderIconSize = this.messageSettings.placeholderIconSize || "25px";

        // Skip on the below if statements,
        // if the isMessagesRestored is true, meaning its not a fresh message
        // SD-6819: So we don't need to show the modal (auto open) for the maintain state messages
        if (!store.state.isMessagesRestored) {
          // Set the show modal property
          if (this.messageSettings.modalLoadBehavior === LoadBehaviorEnum.AUTO_OPEN_WITH_PLACEHOLDER) {
            this.showModal = true;
          } else if (this.messageSettings.modalLoadBehavior === LoadBehaviorEnum.AUTO_OPEN_WITHOUT_PLACEHOLDER) {
            this.showModal = true;
          } else {
            this.showModal = false;
          }
        }
			}

			// Check if the placeholder icon image exists
			if (!Util.isNullOrEmpty(this.placeholderIcon)) {
				this.isPlaceholderIconImage = true;
				/*this.checkIfImageExists(this.placeholderIcon, (exists: any) => {
                    if (exists) {
                        // Success code
                        this.isPlaceholderIconImage = true;
                    } else {
                        // Fail code
                        this.isPlaceholderIconImage = false;
                        this.placeholderIcon = this.getSvgIcon();
                    }
                });
                */
			} else {
				this.isPlaceholderIconImage = false;
				this.placeholderIcon = this.getSvgIcon();
			}
		} catch (ex) {
			console.log("[modal.ts] An error has occurred \n-> Inside setModalSettings -> Error: ", ex);
		}
	}

	/**
	 * This method is called when the user clicks the close modal button.
	 */
	closeModal(id) {
		// store.commit(STORE_MUTATION.SetShowModal, {
		//     id: id,
		//     contents: {
		//         structures: [{ showModal: false }]
		//     }
		// } as IMessage);

		// Clear the timeout if it is set
		// if (this.timeoutId) {
		//     clearTimeout(this.timeoutId);
		// }
		// this.timeoutId = setTimeout(() => {
		//     this.showModal = false;
		// }, 350);

		this.showModal = false;
		this.autoClickCancelButton();
	}

	/**
	 * This method is called when ButtonClick event is emitted.
	 */
	hideModal() {
		this.showModal = false;
	}

	/**
	 * This method is used to get the cancel button from the modal form.
	 *
	 * @param label - The label of the cancel button.
	 */
	getCancelHtmlButton(label: string) {
		try {
			if (!label || Util.isNullOrEmpty(label)) return null;
			const modal = this.chatModal;
			if (!modal) return null;

			// Find the button element based on the button label not by id
			const buttons = modal.querySelectorAll(`button.${store.state.prefixClassName}btn`);

			// Find the Cancel button by label text within this form
			const cancelButton = Array.from(buttons).find((button: any) => {
				if (!button.textContent || Util.isNullOrEmpty(button.textContent)) return false;
				return button.textContent.trim() === label.trim();
			});

			return cancelButton;
		} catch (ex) {
			//console.log("[modal.ts] An error has occurred \n-> Inside getCancelHtmlButton -> Error: ", ex);
		}
		return null;
	}

	/**
	 * SD-5939: This method is used to auto click the cancel button in the modal form,
	 * when the `X` icon is clicked to close the modal.
	 */
	autoClickCancelButton() {
		try {
			//components
			if (!this.components || !Array.isArray(this.components) || this.components.length === 0) return;

			// Validate first that component of type form exists
			let formComponent = this.components.find((component) => component.type === MESSAGE_COMPONENT_TYPE.Form);

			// Validate first that component & formFooter exist
			if (!formComponent || !formComponent.formFooter || !Array.isArray(formComponent.formFooter)) return;

			// Find the first button whose settings has isCancelAction=true
			let cancelButton = formComponent.formFooter.find((item) => {
				// Check if the item is a button and has isCancelAction=true
				if (
					item.type === MESSAGE_COMPONENT_TYPE.Button &&
					item.clickAction &&
					item.clickAction.settings &&
					"isCancelAction" in item.clickAction.settings &&
					item.clickAction.settings.isCancelAction === true
				) {
					return true;
				}
				return false; // if not found return false
			});

			let cancelHtmlButton = this.getCancelHtmlButton(`${cancelButton.label ? cancelButton.label : ""}`);

			// Check if the cancel button is found
			if (cancelButton && cancelHtmlButton) {
				// Click the cancel button
				(cancelHtmlButton as HTMLButtonElement).click();
			}
		} catch (ex) {
			console.log("[modal.ts] An error has occurred \n-> Inside autoClickCancelButton -> Error: ", ex);
		}
	}

	/**
	 * This method is called when the user clicks the open modal message bubble.
	 */
	openModal() {
		this.showModal = true;
	}

	marked(value) {
		return marked(value);
	}

	decodeTags(content: string = "") {
		return content.replace(/&amp;/g, "&").replace(/&amp;/g, "&");
	}

	/**
	 * The function returns a class name by concatenating a prefix with a given value.
	 * @param value - The value parameter is a string representing the class name that you want to
	 * append to the prefixClassName.
	 * @returns the concatenation of the value parameter and the prefixClassName property of the
	 * store.state object.
	 */
	getClassName(value) {
		return store.state.prefixClassName + value;
	}

	@Watch("showModal", { immediate: true })
	onPropertyChanged(value: any) {
		try {
			let userAgentString = navigator.userAgent.toLowerCase();
			let safariIndex = userAgentString.indexOf("safari");

			// SD-5847: Check if the chat page is opened in an iOS WK Webview
			const standalone = "standalone" in navigator && navigator["standalone"];
			const safari = /safari/.test(userAgentString);
			const ios = /iphone|ipod|ipad|macintosh/.test(userAgentString);

			// SD-10057: Add the modal-show class to all normal browsers and iOS WK Webview
			if ((document.body != null) || (ios && !standalone && !safari)) {
				// If the modal is shown, add the class to the body
				if (value) {
					document.body.classList.add(this.getClassName("modal-show"));
				} else {
					// If the modal is hidden, remove the class from the body
					if (document.body.classList.contains(this.getClassName("modal-show"))) {
						document.body.classList.remove(this.getClassName("modal-show"));
						__vueEvent.emit(EVENTS.ScrollToNewMessageTop);
					}
				}
			}

			// Update the modal height if the modal is shown
			if (value) {
				this.updateModalHeight();

				// Clear the form inputs if the modal is shown first time
				store.commit(STORE_MUTATION.ClearFormInputs);

				// SD-5890: If the modal is opened, emit the blur event to blur the input area after some delay
				setTimeout(() => {
					__vueEvent.emit(EVENTS.BlurInputArea);
					this.focusOnModalOpen();
				}, 200);
			}
		} catch (ex) {
			console.log("[modal.ts] An error has occurred \n-> Inside onPropertyChanged -> Error: ", ex);
		}
	}
	
	/**
	 * This method is used to focus on the modal when it is opened.
	 */
	focusOnModalOpen() {
		try {
			if (isMobile.any()) return; // Skip on mobile devices

			const modal = this.chatModal;
			if (!modal) return null;

			// Ensure modal is focusable host
			if (!modal.hasAttribute("tabindex")) modal.setAttribute("tabindex", "-1");

			// Move focus inside the modal
			modal.focus({ preventScroll: true });
		} catch {}
	}

	/**
	 * The function sets a CSS property on the document's root element if the value is truthy.
	 * @param {string} propertyName - A string representing the name of the CSS property to be set.
	 * @param value - The value parameter is the value that you want to set for the specified CSS
	 * property.
	 */
	setPropertyIfExist(propertyName: string, value) {
		if (value) {
			document.documentElement.style.setProperty(propertyName, `${value * 100}%`);
		}
	}

	/**
	 * The function removes a CSS property from the root element of the document if it exists.
	 * @param {string} propertyName - The name of the CSS property that you want to remove.
	 */
	removePropertyIfExist(propertyName: string) {
		let compStyles = window.getComputedStyle(document.documentElement);
		if (!compStyles) return;
		const propertyValue = compStyles.getPropertyValue(propertyName);
		if (!Util.isNullOrEmpty(propertyValue)) {
			document.documentElement.style.removeProperty(propertyName);
		}
	}

	/**
	 * This method is used to update the css variable named `--modalHeightDesktop` and `--modalHeightMobile` in the root element.
	 */
	updateModalHeight() {
		try {
			let compStyles = window.getComputedStyle(document.documentElement);
			if (compStyles && this.messageSettings && Object.keys(this.messageSettings).length !== 0) {
				let isHeightExist = this.messageSettings.height || 0;
				let isHeightDesktopExist = this.messageSettings.heightDesktop || 0;
				let isHeightMobileExist = this.messageSettings.heightMobile || 0;

				// Set modal height for desktop
				if (isHeightDesktopExist || isHeightExist) {
                    this.setPropertyIfExist('--modalHeightDesktop', this.messageSettings.heightDesktop || this.messageSettings.height);
				} else {
                    this.removePropertyIfExist('--modalHeightDesktop');
				}

				if (isHeightMobileExist || isHeightExist) {
                    this.setPropertyIfExist('--modalHeightMobile', this.messageSettings.heightMobile || this.messageSettings.height);
				} else {
                    this.removePropertyIfExist('--modalHeightMobile');
				}
			}
		} catch (ex) {
			console.log("[modal.ts] An error has occurred \n-> Inside updateModalHeight -> Error: ", ex);
		}
	}

	/**
	 * Retrieves an array of focusable elements within the chat modal.
	 *
	 * @returns An array of focusable elements within the chat modal.
	 */
	getFocusableElements(): Element[] {
		let focusableElements: Element[] = null;
		try {
			const modal = this.chatModal;
			if (!modal) return null;

			let focusableElementsString =
				'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
			let focusableElementsList = modal.querySelectorAll(focusableElementsString);

			// Ignore hidden elements
			let focusableElementsArray = Array.from(focusableElementsList).filter(
				(el) => (el as HTMLElement).offsetParent !== null
			);

			focusableElements = focusableElementsArray || [];
		} catch (ex) {
			console.log("[modal.ts] An error has occurred \n-> getFocusableElements -> Error: ", ex);
			focusableElements = null;
		}
		return focusableElements;
	}

	/**
	 * SD-6381: Handles the tab focus event.
	 *
	 * @param event - The keyboard event.
	 */
	handleTabFocus(event: KeyboardEvent) {
		try {
			let focusableElements = this.getFocusableElements();

			// Check if the tab key is pressed and if there are focusable elements
			const isTabPressed = event.key === "Tab";
			if (!isTabPressed || !focusableElements || focusableElements.length === 0) return;

			// Get the first and last focusable elements
			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];

			// Check if the shift key is pressed
			const isShiftPressed = event.shiftKey;

			// and if the first or last element is focused
			// 1. If the first element is focused and the shift + tab key is pressed, focus on the last element
			// 2. If the last element is focused and the tab key is pressed, focus on the first element
			// This is done to fix the tab focus issue in the modal
			if (isShiftPressed) {
				if (document.activeElement === firstElement && lastElement) {
					event.preventDefault();
					(lastElement as HTMLElement).focus();
				}
			} else {
				if (document.activeElement === lastElement && firstElement) {
					event.preventDefault();
					(firstElement as HTMLElement).focus();
				}
			}
		} catch (ex) {
			console.log("[modal.ts] An error has occurred \n-> handleTabFocus -> Error: ", ex);
		}
	}

	/**
	 * Handles the keydown event for the modal.
	 */
	handleModalKeydown() {
		try {
			const modal = this.chatModal;
			if (!modal) return null;

			// Add event listener
			modal.addEventListener("keydown", this.handleTabFocus);
		} catch (ex) {
			console.log("[modal.ts] An error has occurred \n-> handleModalKeydown -> Error: ", ex);
		}
	}

  /**
   * Listens for events from an iframe and handles specific actions.
   * SD-5956: This method listens for the iframe cancel action event and closes the modal.
   */
  listenEventsForIFrame() {
    try {
      if (window.addEventListener) {
        window.addEventListener("message", (event) => {
          if (event.data && event.data.event_id === "satisfi_iframe_cancel_action") {
            this.showModal = false;
          }
        }, false);
      }
    } catch (ex) {
			console.log("[modal.ts] An error has occurred \n-> listenEventsForIFrame -> Error: ", ex);
		}
  }

	//#endregion
}

//#endregion
